import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Checkbox, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import ScheduleService, {
  DAY_LABEL, DayVolume, ScheduleDay, ScheduleWeek, WeekDay, WeekDriver,
} from '../../api/services/schedule.service';
import { describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import ToneChip, { Tone } from '../../components/ui/ToneChip';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { useAuthStore } from '../../store/authStore';
import { count, shortDate } from '../payroll/payrollUI';
import DayCell from './DayCell';
import SchedulePanel from './SchedulePanel';
import {
  driverChanges, roomLeft, shiftWeek, toggleDay, verdict, weekEndingFor,
} from './scheduleMath';

// The week, laid out like the "Weekly Work Schedule vs Dispatch Plan"
// sheet: the roster with a 1 for each day worked, and the totals under it,
// with the volume beneath so the question it exists for — are we above or
// below what is coming — is answered on the same screen.
//
// A change here is this week only. A week with a statement is history: it
// shows what each driver actually did and cannot be edited.

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const DAY_WIDTH = 74;

const STATUS: Record<ScheduleWeek['status'], { label: string; tone: Tone }> = {
  actual: { label: 'Actual', tone: 'ok' },
  forecast: { label: 'Expected', tone: 'info' },
  none: { label: 'No data', tone: 'neutral' },
};

const FootRow: React.FC<{
  label: React.ReactNode;
  days: WeekDay[];
  value: (d: WeekDay) => React.ReactNode;
  tone?: (d: WeekDay) => string | undefined;
  strong?: boolean;
}> = ({ label, days, value, tone, strong }) => (
  <TableRow>
    <TableCell colSpan={4} align="right" sx={{ py: 0.5, px: 1.25, fontWeight: strong ? 700 : 500 }}>{label}</TableCell>
    {days.map((d) => (
      <TableCell key={d.day} align="center" sx={{ ...num, py: 0.5, px: 0.5, fontWeight: strong ? 700 : 500, color: tone?.(d) }}>
        {value(d)}
      </TableCell>
    ))}
  </TableRow>
);

const roomTone = (left: number, withPickups: number) => {
  if (left < 0) return 'error.main';
  return withPickups < 0 ? 'warning.dark' : undefined;
};

const ROOM_HINT = 'Capacity minus delivery and e-commerce, like the sheet. Amber: it fits only if pickups do not count against capacity.';

const volumeOr = (d: WeekDay, read: (v: DayVolume) => number) => (d.volume ? count(read(d.volume)) : '—');

const names = (days: WeekDay[]) => days.map((d) => DAY_LABEL[d.day]).join(', ');

const ScheduleWeekPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { canArea } = useAuthStore();
  const thisWeek = weekEndingFor(new Date());
  const weekEnding = params.get('week') || thisWeek;

  const [week, setWeek] = useState<ScheduleWeek | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [growth, setGrowth] = useState('');

  // Clicks can outrun the server; saves go one at a time, in order, and
  // only the last answer is shown.
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const latest = useRef(0);

  const load = useCallback(async () => {
    setWeek(null);
    try {
      const body = await ScheduleService.week(weekEnding);
      setWeek(body);
      setGrowth(body.weekGrowthPercent === null || body.weekGrowthPercent === undefined ? '' : String(body.weekGrowthPercent));
      setError(null);
    } catch (err) {
      setError(describeError(err, 'Could not load this week.'));
    }
  }, [weekEnding]);
  useEffect(() => { load(); }, [load]);

  const save = (next: ScheduleWeek, growthPercent: number | null) => {
    setWeek(next);
    latest.current += 1;
    const ticket = latest.current;
    const changes = { drivers: driverChanges(next.drivers), closedDays: next.closedDays, growthPercent };
    queue.current = queue.current.then(async () => {
      try {
        const body = await ScheduleService.saveWeek(weekEnding, changes);
        if (ticket === latest.current) setWeek(body);
      } catch (err) {
        setError(describeError(err, 'That change was not saved.'));
        if (ticket === latest.current) load();
      }
    });
  };

  const editable = !!week?.plan && week.status !== 'actual' && canArea('operations', 'edit');
  const weekGrowth = () => (growth.trim() === '' ? null : Number(growth));

  const toggle = (driver: WeekDriver, day: ScheduleDay) => {
    if (!week) return;
    save({
      ...week,
      drivers: week.drivers.map((d) => (d.accountId === driver.accountId ? { ...d, days: toggleDay(d.days, day), changed: true } : d)),
    }, weekGrowth());
  };
  const toggleClosed = (day: ScheduleDay) => {
    if (week) save({ ...week, closedDays: toggleDay(week.closedDays, day) }, weekGrowth());
  };
  const commitGrowth = () => {
    if (week && weekGrowth() !== (week.weekGrowthPercent ?? null)) save(week, weekGrowth());
  };

  const go = (target: string) => setParams(target === thisWeek ? {} : { week: target });

  const short = week?.plan ? week.days.filter((d) => verdict(d) === 'short') : [];
  const tight = week?.plan ? week.days.filter((d) => verdict(d) === 'tight') : [];

  return (
    <PageBody fluid>
      <SchedulePanel />
      <PageHeader
        title={`Week ending ${shortDate(`${weekEnding}T00:00:00Z`)}`}
        subtitle="Saturday to Friday. Changes here apply to this week only."
        actions={(
          <Stack direction="row" gap={0.5} alignItems="center">
            <Button size="small" aria-label="Previous week" onClick={() => go(shiftWeek(weekEnding, -1))} sx={{ minWidth: 34 }}><ChevronLeft /></Button>
            <Button size="small" variant="outlined" disabled={weekEnding === thisWeek} onClick={() => go(thisWeek)}>This week</Button>
            <Button size="small" aria-label="Next week" onClick={() => go(shiftWeek(weekEnding, 1))} sx={{ minWidth: 34 }}><ChevronRight /></Button>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {!week ? (error ? null : <LoadingState />) : !week.plan ? (
        <EmptyState
          title="No schedule yet"
          description="Set up the usual week once. It starts from your statements and routes, and every week follows it."
          action={{ label: 'Set up the usual week', onClick: () => navigate('/schedule/usual') }}
        />
      ) : (
        <Stack gap={2}>
          <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
            <ToneChip tone={STATUS[week.status].tone} label={STATUS[week.status].label} />
            {week.status !== 'none' && (
              <Typography variant="body2" color={short.length ? 'error.main' : 'text.secondary'} sx={{ fontWeight: short.length ? 600 : 400 }}>
                {short.length ? `Over capacity on ${names(short)}.` : 'Within capacity every day.'}
                {tight.length > 0 && ` ${names(tight)}: tight once pickups are counted.`}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {week.status === 'forecast' && (
              <Tooltip title="Expected volume is the latest actuals raised by this much. Blank follows the usual week. Raise it for peak.">
                <TextField
                  size="small" type="number" label="Expected %" value={growth} placeholder={String(week.growthPercent)}
                  disabled={!editable} InputLabelProps={{ shrink: true }}
                  onChange={(e) => setGrowth(e.target.value)} onBlur={commitGrowth}
                  sx={{ width: 110 }}
                />
              </Tooltip>
            )}
          </Stack>

          <Card sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 900, tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: 62 }} />
                  <col style={{ width: 66 }} />
                  <col style={{ width: 80 }} />
                  {week.days.map((d) => <col key={d.day} style={{ width: DAY_WIDTH }} />)}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 1.25 }}>Name</TableCell>
                    <TableCell align="right" sx={{ px: 1 }}><Tooltip title="Work area (route). Blank is a flexible driver."><span>WA</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ px: 1 }}><Tooltip title="Most stops in a day"><span>Stops</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ px: 1 }}><Tooltip title="Most packages in a day"><span>Pkgs</span></Tooltip></TableCell>
                    {week.days.map((d) => (
                      <TableCell key={d.day} align="center" sx={{ px: 0.5 }}>
                        {DAY_LABEL[d.day]}
                        <Typography component="div" variant="caption" color="text.secondary" sx={{ textTransform: 'none', fontWeight: 400 }}>{shortDate(d.date)}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {week.drivers.map((driver) => (
                    <TableRow key={driver.accountId} hover>
                      <TableCell sx={{ py: 0.25, px: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {driver.name}
                        {driver.role === 'BC' && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>BC</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ ...num, py: 0.25, px: 1 }}>{driver.workArea || ''}</TableCell>
                      <TableCell align="right" sx={{ ...num, py: 0.25, px: 1 }}>{driver.maxStops}</TableCell>
                      <TableCell align="right" sx={{ ...num, py: 0.25, px: 1 }}>{driver.maxPackages}</TableCell>
                      {week.days.map(({ day, closed }) => {
                        const on = driver.days.includes(day);
                        if (week.status === 'actual') {
                          const drove = driver.driven[day];
                          return (
                            <TableCell key={day} align="center" sx={{ ...num, py: 0.25, px: 0.5, color: on ? 'text.primary' : 'warning.dark' }}>
                              {drove ? (
                                <Tooltip title={`${count(drove.stops)} stops, ${count(drove.packages)} packages${on ? '' : ' (not scheduled)'}`}><span>{drove.stops}</span></Tooltip>
                              ) : on ? <Tooltip title="Scheduled, nothing on the statement"><Box component="span" sx={{ color: 'text.disabled' }}>–</Box></Tooltip> : ''}
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={day} align="center" sx={{ py: 0.25, px: 0.5 }}>
                            <DayCell
                              on={on && !closed}
                              label={`${driver.name}, ${DAY_LABEL[day]}`}
                              disabled={!editable || closed}
                              onToggle={() => toggle(driver, day)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                  <FootRow label="Scheduled drivers" days={week.days} strong value={(d) => d.scheduled} />
                  <FootRow
                    label={<Tooltip title="Scheduled drivers minus the routes on the dispatch plan"><span>Vs dispatch plan</span></Tooltip>}
                    days={week.days} value={(d) => (d.spare > 0 ? `+${d.spare}` : d.spare)}
                    tone={(d) => (d.spare < 0 ? 'error.main' : undefined)}
                  />
                  <FootRow label="Max stops" days={week.days} strong value={(d) => count(d.maxStops)} />
                  <FootRow label={week.status === 'actual' ? 'Stops' : 'Expected stops'} days={week.days} value={(d) => volumeOr(d, (v) => v.stops)} />
                  <FootRow label="Pickup stops" days={week.days} value={(d) => volumeOr(d, (v) => v.pickupStops)} />
                  <FootRow
                    label={<Tooltip title={ROOM_HINT}><span>Stops left</span></Tooltip>} days={week.days} strong
                    value={(d) => (d.volume ? count(roomLeft(d, d.volume).stops) : '—')}
                    tone={(d) => { if (!d.volume) return undefined; const r = roomLeft(d, d.volume); return roomTone(r.stops, r.stopsWithPickups); }}
                  />
                  <FootRow label="Max packages" days={week.days} strong value={(d) => count(d.maxPackages)} />
                  <FootRow label={week.status === 'actual' ? 'Packages' : 'Expected packages'} days={week.days} value={(d) => volumeOr(d, (v) => v.packages)} />
                  <FootRow label="Pickup packages" days={week.days} value={(d) => volumeOr(d, (v) => v.pickupPackages)} />
                  <FootRow
                    label={<Tooltip title={ROOM_HINT}><span>Packages left</span></Tooltip>} days={week.days} strong
                    value={(d) => (d.volume ? count(roomLeft(d, d.volume).packages) : '—')}
                    tone={(d) => { if (!d.volume) return undefined; const r = roomLeft(d, d.volume); return roomTone(r.packages, r.packagesWithPickups); }}
                  />
                  {week.status !== 'actual' && (
                    <FootRow
                      label={<Tooltip title="A holiday: nobody works and nothing is expected"><span>Closed</span></Tooltip>} days={week.days}
                      value={(d) => (
                        <Checkbox
                          size="small" checked={d.closed} disabled={!editable} sx={{ p: 0.25 }}
                          inputProps={{ 'aria-label': `Closed ${DAY_LABEL[d.day]}` }}
                          onChange={() => toggleClosed(d.day)}
                        />
                      )}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      )}
    </PageBody>
  );
};

export default ScheduleWeekPage;
